import {TrashIcon, PlusIcon} from '@primer/octicons-react'
import {Button, FormControl, IconButton, TextInput} from '@primer/react'
import {useId, useRef} from 'react'

import styles from './KeyValueFields.module.css'

export interface KeyValuePair {
  /**
   * Stable identity for React. Rows shift when one is removed, and an index key
   * would make the inputs below it adopt their neighbour's state.
   */
  id: string
  key: string
  value: string
}

export interface KeyValueFieldsProps {
  legend: string
  caption?: React.ReactNode
  keyLabel: string
  keyPlaceholder?: string
  valuePlaceholder?: string
  /** Label for the add button. Defaults to the legend, lowercased. */
  addLabel?: string
  pairs: KeyValuePair[]
  onChange: (pairs: KeyValuePair[]) => void
  busy?: boolean
  errors?: Record<string, string>
  showErrors?: boolean
  errorRef?: React.RefObject<HTMLInputElement | null>
}

let nextPairId = 0
const NO_ERRORS: Record<string, string> = {}

export function newPair(): KeyValuePair {
  nextPairId += 1
  return {id: `pair-${nextPairId}`, key: '', value: ''}
}

/** Drops blank rows, which the form keeps around while the user is typing. */
export function pairsToRecord(pairs: KeyValuePair[]): Record<string, string> | undefined {
  const entries = pairs.filter(pair => pair.key.trim()).map(pair => [pair.key.trim(), pair.value] as const)
  if (entries.length === 0) return undefined
  return Object.fromEntries(entries)
}

export function recordToPairs(record: Record<string, string> | undefined): KeyValuePair[] {
  if (!record) return []
  return Object.entries(record).map(([key, value]) => ({...newPair(), key, value}))
}

export function validatePairs(pairs: KeyValuePair[], caseInsensitive: boolean): Record<string, string> {
  const errors: Record<string, string> = {}
  const seen = new Set<string>()
  for (const pair of pairs) {
    const key = caseInsensitive ? pair.key.trim().toLowerCase() : pair.key.trim()
    if (!key && pair.value) errors[pair.id] = 'Enter a name for this value.'
    if (key && seen.has(key)) errors[pair.id] = 'Use each name only once.'
    seen.add(key)
  }
  return errors
}

export function KeyValueFields({
  legend,
  caption,
  keyLabel,
  keyPlaceholder,
  valuePlaceholder,
  addLabel,
  pairs,
  onChange,
  busy = false,
  errors = NO_ERRORS,
  showErrors = false,
  errorRef,
}: KeyValueFieldsProps) {
  const captionId = useId()
  const addRef = useRef<HTMLButtonElement>(null)
  const removeButtonsRef = useRef(new Map<string, HTMLButtonElement>())
  const addedIdRef = useRef<string | null>(null)
  const setInputRef = (element: HTMLInputElement | null, id: string) => {
    if (id === Object.keys(errors)[0] && errorRef) errorRef.current = element
    if (element && addedIdRef.current === id) {
      element.focus()
      addedIdRef.current = null
    }
  }

  const updateAt = (index: number, patch: Partial<KeyValuePair>) => {
    if (busy) return
    onChange(pairs.map((pair, current) => (current === index ? {...pair, ...patch} : pair)))
  }

  const removeAt = (index: number) => {
    if (busy) return
    const previous = pairs[index - 1]
    const target = previous ? removeButtonsRef.current.get(previous.id) : addRef.current
    target?.focus()
    onChange(pairs.filter((_pair, current) => current !== index))
  }

  return (
    <fieldset className={styles.fieldset} aria-describedby={caption ? captionId : undefined}>
      <legend className={styles.legend}>{legend}</legend>
      {caption ? (
        <p className={styles.caption} id={captionId}>
          {caption}
        </p>
      ) : null}

      {pairs.length > 0 ? (
        <ul className={styles.rows}>
          {pairs.map((pair, index) => (
            <li key={pair.id} className={styles.row}>
              <FormControl className={styles.field}>
                <FormControl.Label>{`${keyLabel} ${index + 1}`}</FormControl.Label>
                <TextInput
                  ref={element => setInputRef(element, pair.id)}
                  readOnly={busy}
                  value={pair.key}
                  onChange={event => updateAt(index, {key: event.target.value})}
                  placeholder={keyPlaceholder}
                  block
                />
                {showErrors && errors[pair.id] ? (
                  <FormControl.Validation variant="error">{errors[pair.id]}</FormControl.Validation>
                ) : null}
              </FormControl>
              <FormControl className={styles.field}>
                <FormControl.Label>{`Value ${index + 1}`}</FormControl.Label>
                <TextInput
                  readOnly={busy}
                  value={pair.value}
                  onChange={event => updateAt(index, {value: event.target.value})}
                  placeholder={valuePlaceholder}
                  block
                />
              </FormControl>
              <IconButton
                className={styles.remove}
                ref={(element: HTMLButtonElement | null) => {
                  if (element) removeButtonsRef.current.set(pair.id, element)
                  else removeButtonsRef.current.delete(pair.id)
                }}
                inactive={busy}
                aria-disabled={busy}
                icon={TrashIcon}
                variant="invisible"
                aria-label={`Remove ${pair.key.trim() || `${keyLabel} ${index + 1}`}`}
                onClick={() => removeAt(index)}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <Button
        ref={addRef}
        inactive={busy}
        aria-disabled={busy}
        size="small"
        variant="invisible"
        leadingVisual={PlusIcon}
        className={styles.add}
        onClick={() => {
          if (busy) return
          const pair = newPair()
          addedIdRef.current = pair.id
          onChange([...pairs, pair])
        }}
      >
        {addLabel ?? `Add ${legend.toLowerCase()}`}
      </Button>
    </fieldset>
  )
}
